// English strings for P7-E (AUTOMATION-01…04) (D-013: English only). Owned by that worker;
// spread into `en` as `en.automation`, so keys read `t("automation.…")`. Copy rules: DESIGN_SYSTEM §9.6.
// Sections: `flows` (marketing/automations: list, builder, enable, runs) and `campaigns`
// (marketing/messages: draft, estimate, send, progress, results).
export const automationStrings = {
  flows: {
    title: "Automations",
    lead: "Send a message when something happens: a checkout left unfinished, or an order delivered. Messages respect consent, quiet hours (8 PM to 8 AM Nepal time) and a limit per customer.",
    create: "New automation",
    caption: "Automations",
    emptyTitle: "No automations yet",
    emptyBody: "Start with an unfinished-checkout reminder or a thank-you coupon after delivery.",
    columns: {
      name: "Name",
      trigger: "When",
      channel: "Channel",
      steps: "Messages",
      state: "State",
    },
    triggers: {
      abandoned_checkout: "Checkout left unfinished",
      order_delivered: "Order delivered",
    },
    triggerHelp: {
      abandoned_checkout:
        "Only shoppers who ticked “message me about this order” are contacted. Contact details are deleted after 30 days.",
      order_delivered: "Runs once per delivered order. Test orders never count.",
    },
    channels: { sms: "SMS", email: "Email" },
    states: { draft: "Draft", active: "On", disabled: "Off" },
    stepsCount: { one: "{count} message", other: "{count} messages" },
    enable: "Turn on",
    disable: "Turn off",
    edit: "Edit",
    remove: "Delete",
    runs: "Results",
    enabled: "Automation turned on.",
    disabled: "Automation turned off.",
    saved: "Automation saved.",
    deleted: "Automation deleted.",
    removeTitle: "Delete “{name}”?",
    removeBody:
      "Pending messages for this automation are cancelled. Messages already sent stay in the log.",
    entitlement: {
      label: "Automations on your plan",
      value: "{used} of {limit} on",
      unlimited: "{used} on, no limit",
      locked:
        "Your plan does not include automations. You can build and save them; turning one on needs a plan that includes them.",
      full: "You have reached your plan's limit. Turn another automation off first.",
    },
    form: {
      titleNew: "New automation",
      titleEdit: "Edit automation",
      name: "Name",
      trigger: "When",
      channel: "Send by",
      steps: "Messages",
      delay: "Wait (minutes)",
      delayHelpFirst: "After the trigger. Unfinished checkouts need at least 30 minutes.",
      delayHelpNext: "After the previous message.",
      body: "Message",
      bodyHelp: "You can use {storeLink}; after delivery also {couponCode}.",
      addStep: "Add a message",
      removeStep: "Remove",
      quietHours: "Respect quiet hours",
      quietHoursHelp: "A message due between 8 PM and 8 AM Nepal time waits until 8 AM.",
      cap: "Limit per customer",
      capCount: "At most (messages)",
      capWindow: "Within (hours)",
      capHelp: "Counts every automated message to the same phone or email.",
      noCap: "No limit",
      minOrder: "Only orders of at least",
      coupon: "Thank-you coupon",
      couponOn: "Include a single-use coupon",
      couponType: "Discount",
      couponPercent: "Percent off",
      couponFixed: "Amount off",
      couponValue: "Value",
      couponValidity: "Valid for (days)",
      couponMinOrder: "Coupon minimum order",
      save: "Save",
      invalid: "Check this field.",
      errors: {
        min_30: "Wait at least 30 minutes before the first reminder.",
        unknown_placeholder: "Only the store link and coupon code placeholders can be used.",
        coupon_code_needs_coupon: "Turn on the coupon to use the coupon code placeholder.",
        coupon_needs_order_delivered: "Coupons are only for delivered orders.",
        percent_range: "Use 1 to 100 percent.",
      },
    },
    // Compact builder and results popups (owner, 2026-10-22).
    compact: {
      now: "Right away",
      days: { one: "{count} day", other: "{count} days" },
      hours: "{h} h",
      minutes: "{m} min",
      min: "min",
      namePh: "Name, e.g. Unfinished checkout reminder",
      messagePh: "Message, e.g. You left something in your cart: {storeLink}",
      waitFirst: "Send after",
      waitNext: "Then after",
      quietHint: "Waits until 8 AM if due at night",
      capHint: "Counts every automated message",
      couponHint: "A new code for each customer",
      capLine: "{count} per {hours} h",
      lastAfter: "Last message after",
      onSince: "On since",
      hint: "Only customers who agreed to messages get them. Messages cost SMS credits from your wallet.",
      sec: { basics: "Automation", rules: "Rules" },
      why: { name: "Name the automation.", message: "Write every message." },
      tiles: {
        on: "Turned on",
        onHint: "Sending now",
        cartHint: "Bring shoppers back",
        deliveredHint: "Thank and invite back",
      },
    },
    preview: {
      title: "Cost preview",
      perCustomer: "Per customer: {cost}",
      month: {
        one: "The last 30 days would have been {count} message, about {cost}.",
        other: "The last 30 days would have been {count} messages, about {cost}.",
      },
      activation:
        "Cost preview is not available yet: messaging is waiting for activation ({dependency}).",
    },
    results: {
      title: "Results: {name}",
      empty: "Nothing has run yet.",
      columns: {
        subject: "For",
        state: "State",
        result: "Last result",
        sent: "Sent",
        coupon: "Coupon",
        due: "Next step",
      },
      subjects: { cart: "Checkout", order: "Order" },
      states: {
        pending: "Waiting",
        completed: "Finished",
        cancelled: "Stopped",
        blocked: "Blocked",
      },
      outcomes: {
        queued: "Sent",
        duplicate: "Sent",
        suppressed: "Not sent",
        insufficient_balance: "Not enough wallet balance",
        activation_required: "Blocked: activation required",
      },
      reasons: {
        no_consent: "no marketing consent",
        unsubscribed: "unsubscribed",
        quiet_hours: "waiting for quiet hours to end",
        frequency_cap: "limit per customer reached",
        converted: "the customer placed the order",
        no_contact: "no contact details",
        flow_inactive: "automation turned off",
        flow_deleted: "automation deleted",
        order_cancelled: "order cancelled",
        subject_missing: "no longer exists",
        plan: "not in your current plan",
      },
    },
  },
  campaigns: {
    title: "Messages",
    lead: "Send an SMS or email to a customer segment. Only customers who agreed to marketing receive it, and the cost comes from your wallet.",
    create: "New message",
    caption: "Messages",
    emptyTitle: "No messages yet",
    emptyBody: "Write a message, pick a segment and check the cost before you send.",
    columns: {
      name: "Name",
      channel: "Channel",
      state: "State",
      recipients: "Recipients",
      cost: "Cost",
    },
    states: {
      draft: "Draft",
      sending: "Sending",
      completed: "Sent",
      blocked: "Blocked",
      blockedActivation: "Blocked: activation required",
    },
    form: {
      title: "New message",
      name: "Name",
      segment: "Segment ID",
      segmentHelp: "The customer segment to send to (from Customers, Segments).",
      channel: "Send by",
      body: "Message",
      bodyHelp: "You can use {storeLink}. Say how to opt out, for example “Reply STOP to opt out”.",
      save: "Save draft",
    },
    estimate: "Check cost",
    send: "Send",
    sendTitle: "Send “{name}”?",
    sendBody: {
      one: "{count} customer will receive it. {cost} is held from your wallet now; only messages that go out are charged, and the rest is returned.",
      other:
        "{count} customers will receive it. {cost} is held from your wallet now; only messages that go out are charged, and the rest is returned.",
    },
    estimateResult: {
      one: "{count} customer, {cost}. {excluded} left out (no consent, opted out or no contact details).",
      other:
        "{count} customers, {cost}. {excluded} left out (no consent, opted out or no contact details).",
    },
    noEstimate: "Check the cost before sending.",
    created: "Draft saved.",
    sent: "Sending started.",
    insufficient: "Your wallet balance cannot cover this message. Available: {available}.",
    activation: "Messaging is waiting for activation ({dependency}). Nothing was sent or charged.",
    progress: "{queued} sent, {excluded} left out, {failed} failed",
    wallet: "Held {reserved}, charged {debited}, returned {released}",
    open: "Details",
    detailTitle: "Message: {name}",
    // Compact popups (owner, 2026-10-22).
    compact: {
      namePh: "Name, e.g. Tihar sale announcement",
      bodyPh: "Message, e.g. Tihar sale: 20% off till Sunday. {storeLink}",
      sec: { message: "Basics", to: "Send to", result: "Result" },
      members: { one: "{count} customer", other: "{count} customers" },
      inSegment: "In the segment",
      noSegments: "No segments yet. Make one in Customers, Segments.",
      length: "{chars} characters · {parts} SMS",
      parts: "SMS per customer",
      unicode: "Nepali letters fit 70 characters in one SMS, so this message uses more SMS.",
      made: "Made",
      hint: "This saves a draft. Check the cost and send it from the next screen.",
      why: { name: "Name the message.", segment: "Pick who gets it.", body: "Write the message." },
      tiles: {
        drafts: "Drafts",
        draftsHint: "Not sent yet",
        sent: "Sent",
        sentHint: "Sending or finished",
        reached: "Messages sent",
        reachedHint: "To customers who agreed",
        spent: "Spent",
        spentHint: "Charged to your wallet",
      },
    },
  },
} as const;
